import * as React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Archive, Undo2 } from 'lucide-react';
import { api, ErroHttp } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Botao } from '@/componentes/ui/botao';
import { Campo } from '@/componentes/ui/primitivos';

/**
 * "Dispensar da esteira": a nota vai para Fora do fluxo, com motivo, e some
 * das listas. Pedido do Paulo em 27/10/2026: a primeira volta em produção
 * trouxe 7 dias de notas, muitas já tratadas à mão no Procfit. Nada no ERP
 * muda; a nota volta com "Trazer de volta à esteira".
 */
export const MOTIVOS_DISPENSA = [
  'Nota antiga, já tratada no Procfit',
  'Não é compra para estoque (uso e consumo, serviço, bonificação)',
  'Duplicada ou substituída por outra nota',
] as const;

export function EscolherMotivo({
  quantas,
  carregando,
  erro,
  aoConfirmar,
  aoCancelar,
}: {
  quantas: number;
  carregando: boolean;
  erro: string | null;
  aoConfirmar: (motivo: string) => void;
  aoCancelar: () => void;
}): JSX.Element {
  const [escolhido, definirEscolhido] = React.useState<string>(MOTIVOS_DISPENSA[0]);
  const [outro, definirOutro] = React.useState('');
  const motivo = escolhido === 'outro' ? outro.trim() : escolhido;
  return (
    <div className="space-y-2.5">
      <p className="text-xs text-textoFraco">
        {quantas > 1 ? `As ${quantas} notas saem` : 'A nota sai'} das listas e vão para <b>Fora do fluxo</b>. Nada muda no Procfit, e dá para trazer
        de volta.
      </p>
      <div className="flex flex-wrap gap-1.5">
        {[...MOTIVOS_DISPENSA, 'outro'].map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => definirEscolhido(m)}
            className={cn(
              'rounded-full border px-2.5 py-1 text-xs font-semibold transition',
              escolhido === m ? 'border-texto bg-texto text-superficie' : 'border-borda bg-superficie text-textoFraco hover:border-bordaForte',
            )}
          >
            {m === 'outro' ? 'Outro motivo…' : m}
          </button>
        ))}
      </div>
      {escolhido === 'outro' ? <Campo autoFocus placeholder="Por que sai da esteira?" value={outro} onChange={(e) => definirOutro(e.target.value)} /> : null}
      {erro ? <p className="text-xs text-status-divergencia">{erro}</p> : null}
      <div className="flex justify-end gap-2">
        <Botao variante="fantasma" tamanho="pequeno" onClick={aoCancelar}>
          Cancelar
        </Botao>
        <Botao variante="secundaria" tamanho="pequeno" disabled={!motivo} carregando={carregando} onClick={() => aoConfirmar(motivo)}>
          <Archive className="h-4 w-4" /> Dispensar{quantas > 1 ? ` ${quantas} notas` : ''}
        </Botao>
      </div>
    </div>
  );
}

const mensagem = (e: unknown): string | null => (e instanceof ErroHttp ? e.message : e ? String(e) : null);

/** Na gaveta: dispensar esta nota, ou trazê-la de volta. */
export function DispensarNota({
  chave,
  podeDispensar,
  podeReincluir,
  motivoAtual,
}: {
  chave: string;
  podeDispensar: boolean;
  podeReincluir: boolean;
  motivoAtual: string | null;
}): JSX.Element | null {
  const cliente = useQueryClient();
  const [aberto, definirAberto] = React.useState(false);
  const depois = (): void => {
    definirAberto(false);
    void cliente.invalidateQueries({ queryKey: ['entrada', chave] });
    void cliente.invalidateQueries({ queryKey: ['entradas'] });
  };
  const dispensar = useMutation({ mutationFn: (motivo: string) => api.post(`/api/entradas/nota/${chave}/fora`, { motivo }), onSuccess: depois });
  const reincluir = useMutation({ mutationFn: () => api.post(`/api/entradas/nota/${chave}/reincluir`, {}), onSuccess: depois });

  if (podeReincluir) {
    return (
      <section className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-borda bg-superficie2 px-4 py-3">
        <p className="text-[13px] text-textoFraco">
          Dispensada da esteira{motivoAtual ? `: ${motivoAtual}` : '.'}
        </p>
        <Botao variante="secundaria" tamanho="pequeno" carregando={reincluir.isPending} onClick={() => reincluir.mutate()}>
          <Undo2 className="h-4 w-4" /> Trazer de volta à esteira
        </Botao>
        {reincluir.error ? <p className="w-full text-xs text-status-divergencia">{mensagem(reincluir.error)}</p> : null}
      </section>
    );
  }
  if (!podeDispensar) return null;
  return aberto ? (
    <section className="rounded-xl border border-borda p-4">
      <p className="rotulo mb-2">Dispensar da esteira</p>
      <EscolherMotivo
        quantas={1}
        carregando={dispensar.isPending}
        erro={mensagem(dispensar.error)}
        aoConfirmar={(m) => dispensar.mutate(m)}
        aoCancelar={() => definirAberto(false)}
      />
    </section>
  ) : (
    <button
      type="button"
      onClick={() => definirAberto(true)}
      className="inline-flex items-center gap-1.5 text-xs font-semibold text-textoFraco hover:text-texto hover:underline"
    >
      <Archive className="h-3.5 w-3.5" /> Não precisa passar pela esteira? Dispensar
    </button>
  );
}
